import { useCallback, useState } from "react";
import { GoogleMap, useJsApiLoader, Polygon } from "@react-google-maps/api";
import styled from "@emotion/styled/macro";
import { Trans, useTranslation } from "react-i18next";
import { Flex } from "@chakra-ui/react";
import { keys } from "lodash";
import { Link } from "react-router-dom";
import MapControl from "./MapControl";
import countries from "./coords";
import { withSize } from "react-sizeme";
import { isMobile } from "react-device-detect";

const containerStyle = {
  width: "100%",
  height: "550px",
};

const CountryInfo = styled.div`
  color: ${(props) => props.theme.colors.primary};
  max-width: 20em;
  line-height: 1.4rem;
  padding: 0.5em;
  font-size: 0.9rem;
`;
const CountryTitle = styled.h3`
  font-weight: bold;
  font-size: 1rem;
  margin-bottom: 0.5em;
`;

const Wrapper = styled.div`
  width: 90%;
  max-width: 1024px;
  margin: 1em auto;
  .gm-style .gm-style-iw-c {
    border-radius: 0;
  }
  div {
    overflow: visible;
  }
`;

const LegendTitle = styled.h2`
  color: ${(props) => props.theme.colors.secondary};
  font-size: 1.5rem;
  margin-bottom: 1rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1.75rem;
    margin-bottom: 2rem;
  }
`;

const LegendWrapper = styled.div`
  z-index: 200;
  background-color: transparent;
  margin: 20px 0 0 20px;
  font-family: ${(props) => props.theme.fonts.heading};
`;

const LegendItem = styled.div`
  color: ${(props) => props.theme.colors.primary};
  display: flex;
  font-size: 1rem;
  margin-bottom: 0.6em;
  line-height: 1rem;
`;

const LegendColor = styled.div`
  width: 27px;
  height: 27px;
  background-color: ${(props) => getFillColorByCoverage(props.coverage)};
  margin-right: 7px;
`;

const ListViewWrapper = styled.div`
  margin-top: 3px;
  padding: 1.7em;
  background-color: ${(props) => props.theme.colors.cultered};
`;
const ListSection = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  h3 {
    font-size: 1.3rem;
    font-weight: bold;
    margin: 1em 0 0.5em;
  }
`;

const ShowButton = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 2px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: 0.25em 1em;
  text-align: center;
  font-weight: bold;
  margin-bottom: 1em;
  min-width: 70%;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    min-width: auto;
  }
`;
const HideButton = styled(ShowButton)`
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
`;

const CountryList = styled.div`
  column-count: 2;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    column-count: 4;
  }
`;

const Info = styled.div`
  margin-bottom: 0.5em;
  font-size: 1.2rem;
`;
const Text = styled.div`
  font-size: 0.8em;
  margin: 1em 0;
  color: ${({ theme }) => theme.colors.primary};
  & a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;
const MobileLegendWrapper = styled.div`
  display: block;
  margin-bottom: -10px;
  padding: 2em 0;
  background-color: #efefef;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;
const Desc = styled.span`
  font-family: ${(props) => props.theme.fonts.body};
`;
const Legend = () => {
  const { t } = useTranslation();

  return (
    <LegendWrapper>
      <LegendTitle>{t("abroad.map.legend.title")}</LegendTitle>
      {["A", "B", "C", "D"].map((coverage) => (
        <LegendItem key={coverage}>
          <LegendColor coverage={coverage} />
          <div>
            {t(`abroad.map.legend.${coverage}.insurance`)}
            <br />
            <Desc>{t(`abroad.map.legend.${coverage}.legend`)}</Desc>
          </div>
        </LegendItem>
      ))}
    </LegendWrapper>
  );
};

const DesktopLegend = styled(Legend)`
  display: none;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;
const MobileLegend = styled(Legend)`
  display: block;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;
const InfoBoxWrap = styled.div`
  display: flex;
  background-color: white;
  bottom: 5%;
  left: 2%;
  position: absolute;
`;
const CloseButton = styled.div`
  display: flex;
  color: black;
  top: 0;
  right: 2%;
  cursor: pointer;
  position: absolute;
`;

const CoverageMap = ({ size }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD-GXoPtXd-J7p9G8dDZCbkDiQFi0WGxsg",
  });
  const [InfoBox, setInfoBox] = useState(null);
  const [listView, setListView] = useState(false); // eslint-disable-next-line
  const [map, setMap] = useState(null);

  const isMobile = size.width < 768;

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const toggleInfoBox = (country) => {
    setInfoBox({ country });
  };

  const polygonOptions = {
    fillOpacity: 1,
    strokeColor: "#efefef",
    strokeOpacity: 1,
    strokeWeight: 1,
  };

  const getInfoText = (countryCode) => {
    const code = getCoverage(countryCode);
    const country = t(`abroad.countries.${countryCode}`);
    const countryFR = t(`abroad.countriesFR.${countryCode}`);
    return t(`abroad.coverage.${code}`, lang === "fr" ? { countryFR } : { country });
  };

  const onCloseInfoBox = () => {
    setInfoBox(null);
  };

  return (
    <Wrapper>
      {isLoaded && (
        <>
          {isMobile && (
            <MobileLegendWrapper>
              <MobileLegend />
            </MobileLegendWrapper>
          )}
          <GoogleMap mapContainerStyle={containerStyle} options={mapOptions} onLoad={onLoad} onUnmount={onUnmount}>
            {!isMobile && (
              <MapControl position={window.google.maps.ControlPosition.LEFT_TOP}>
                <DesktopLegend />
              </MapControl>
            )}

            {countries.map((country) => (
              <Polygon
                key={country.code}
                paths={country.coordinates}
                options={{ ...polygonOptions, fillColor: getFillColorByCountry(country.code) }}
                onMouseOver={(e) => toggleInfoBox(country.code)}
                onMouseOut={onCloseInfoBox}
                onClick={(e) => toggleInfoBox(country.code)}
              />
            ))}

            {InfoBox && (
              <InfoBoxWrap>
                <CountryInfo>
                  <CountryTitle>{t(`abroad.countries.${InfoBox.country}`)}</CountryTitle>
                  {getInfoText(InfoBox.country)}
                  <CloseButton onClick={onCloseInfoBox}>x</CloseButton>
                </CountryInfo>
              </InfoBoxWrap>
            )}
          </GoogleMap>
        </>
      )}

      <ListViewWrapper>
        {listView ? (
          <div>
            <Flex justifyContent="center">
              <HideButton onClick={() => setListView(false)}>{t("abroad.map.list.hide")}</HideButton>
            </Flex>

            {["A", "B", "C", "D"].map((coverage) => (
              <ListSection key={coverage}>
                <div>
                  <h3>{t(`abroad.map.legend.${coverage}.insurance`)}</h3>
                  <Info>{t(`abroad.map.legend.${coverage}.info`)}</Info>
                </div>
                <CountryList>
                  {countriesByCoverage[coverage]
                    .map((countryCode) => t(`abroad.countries.${countryCode}`))
                    .sort()
                    .map((country) => (
                      <div>{country}</div>
                    ))}
                </CountryList>
              </ListSection>
            ))}
            <Text>
              <Trans i18nKey="abroad.map.legend.specialCountries">
                <Link target="_blank" to={{ pathname: "http://gc-territorial-validity.cobx.org" }}>
                  {""}
                </Link>
              </Trans>
            </Text>
          </div>
        ) : (
          <>
            <Text>
              <Trans i18nKey="abroad.map.legend.specialCountries">
                <Link target="_blank" to={{ pathname: "http://gc-territorial-validity.cobx.org" }}>
                  {""}
                </Link>
              </Trans>
            </Text>
            <Flex justifyContent="center">
              <ShowButton onClick={() => setListView(true)}>{t("abroad.map.list.show")}</ShowButton>
            </Flex>
          </>
        )}
      </ListViewWrapper>
    </Wrapper>
  );
};
const center = {
  lat: 53.1,
  lng: isMobile ? 8 : -15,
};
const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  center: center,
  gestureHandling: "cooperative",
  zoom: 3,
  minZoom: 2,
  maxZoom: 22,

  styles: [
    {
      featureType: "administrative",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "administrative.country",
      elementType: "geometry.stroke",
      stylers: [
        {
          color: "#ffffff",
        },
        {
          visibility: "on",
        },
        {
          weight: 1,
        },
      ],
    },
    {
      featureType: "landscape",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "poi",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "poi",
      elementType: "geometry",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "road",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "transit",
      stylers: [
        {
          visibility: "off",
        },
      ],
    },
    {
      featureType: "water",
      stylers: [
        {
          color: "#efefef",
        },
      ],
    },
  ],
};

const countriesByCoverage = {
  A: [
    "AD",
    "AT",
    "BE",
    "BA",
    "BG",
    "CY",
    "CZ",
    "CH",
    "DE",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "GB",
    "HR",
    "HU",
    "IE",
    "IS",
    "IT",
    "LI",
    "LV",
    "LT",
    "LU",
    "MC",
    "ME",
    "MT",
    "NL",
    "NO",
    "PL",
    "PT",
    "RO",
    "SM",
    "RS",
    "SK",
    "SI",
    "SE",
    "VA",
  ],
  B: ["MA", "TR", "TN"],
  C: ["AL", "AZ", "BY", "IR", "IL", "MD", "MK", "RU", "UA"],
  D: [],
};

const getCoverage = (country) => {
  let coverage = "D";
  keys(countriesByCoverage).forEach((key) => {
    if (countriesByCoverage[key].includes(country)) {
      coverage = key;
    }
  });
  return coverage;
};

const getFillColorByCountry = (country) => {
  return getFillColorByCoverage(getCoverage(country));
};

const getFillColorByCoverage = (coverage) => {
  switch (coverage) {
    case "A":
      return "#8AC9B2";
    case "B":
      return "#EB9755";
    case "C":
      return "#C69671";
    default:
      return "#D4D7D6";
  }
};
export default withSize()(CoverageMap);
